import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import {
  Form,
  InputPassword,
  InputText,
  useFormConfig,
  Button,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
  FormTitle,
  displayErrorToast,
  InfoCard,
  Link,
  MaxWidthContent,
  UnconnectedPageTemplate,
} from "@endya-conformite/ui-kit";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { Header } from "@src/components/Header/Header";
import { useSession } from "@src/session/UserSessionContext";
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Login.module.scss";

const schema = zod.object({
  email: zod
    .string({ required_error: "Veuillez saisir votre adresse email." })
    .nonempty("Veuillez saisir votre adresse email.")
    .refine(
      (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(val),
      { message: "Veuillez saisir une adresse email valide." }
    ),
  password: zod
    .string({ required_error: "Veuillez saisir votre mot de passe." })
    .nonempty("Veuillez saisir votre mot de passe."),
});

export type LoginFormValue = zod.infer<typeof schema>;

export function Login() {
  const session = useSession();
  const formConfig = useFormConfig<typeof schema, LoginFormValue>({
    schema,
  });

  const [query] = useSearchParams();
  const urlRedirect = query.get("url");
  const navigate = useNavigate();

  const [shouldForgotPassword, setShouldForgotPassword] = useState(false);
  const [isRadie, setIsRadie] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (submittedData: LoginFormValue): Promise<void> => {
    setIsSubmitting(true);
    try {
      await session.login(submittedData.email, submittedData.password);
    } catch (err) {
      handleGatewayError({
        onResponse: (error) => {
          if (error.response.status === 401) {
            displayErrorToast(
              "L'adresse email ou le mot de passe est incorrect."
            );
            return;
          }
          if (
            error.response.status === 403 &&
            error.response.data.message === "NEW_PASSWORD_REQUIRED"
          ) {
            const userId = error.response.data.cause?.userId as string;
            const sessionToken = error.response.data.cause?.session as string;
            navigate(
              `/new-password/${userId}?url=${
                urlRedirect ?? "/"
              }&session=${sessionToken}`,
              {
                replace: true,
              }
            );
            return;
          }
          if (
            error.response.status === 403 &&
            error.response.data.message === "CHANGE_PASSWORD_TOO_MANY_REQUEST"
          ) {
            setShouldForgotPassword(true);
            return;
          }
          if (
            error.response.status === 403 &&
            error.response.data.message === "COMPANY_RADIE"
          ) {
            setIsRadie(true);
            return;
          }
          displayErrorToast(
            error.response.data.message ||
              "Une erreur est survenue lors de la connexion. Veuillez réessayer."
          );
        },
        onUnhandled: (error) => {
          console.error(error);
          displayErrorToast(
            "Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer."
          );
        },
      })(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (session.isConnected) {
    return <Navigate to={urlRedirect ?? "/"} />;
  }

  return (
    <UnconnectedPageTemplate>
      <Header />
      <MaxWidthContent>
        <Form formConfig={formConfig} onSubmit={handleSubmit}>
          <FormGrid disableHorizontalGutter>
            <FormTitle>
              <h1>Connectez-vous à votre espace</h1>
            </FormTitle>
            {shouldForgotPassword && (
              <FormRow>
                <FormField>
                  <InfoCard type="warning">
                    Votre compte est bloqué suite à trop de tentatives de
                    connexions. Merci de{" "}
                    <Link
                      to={`/forgot-password?email=${
                        formConfig.getValues().email
                      }`}
                    >
                      réinitialiser votre mot de passe
                    </Link>{" "}
                    pour pouvoir vous connecter.
                  </InfoCard>
                </FormField>
              </FormRow>
            )}
            {isRadie && (
              <FormRow>
                <FormField>
                  <InfoCard type="warning">
                    L&apos;accès à votre espace est momentanément indisponible
                    car votre entreprise a été radiée. Cette décision a été
                    notifiée par lettre recommandée. Pour en savoir plus ou
                    régulariser votre situation, notre équipe reste à votre
                    disposition à{" "}
                    <a href="mailto:support@endya.fr">support@endya.fr</a>.
                  </InfoCard>
                </FormField>
              </FormRow>
            )}
            <FormRow>
              <FormField>
                <InputText<LoginFormValue> name="email" label="Adresse email" />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField>
                <InputPassword<LoginFormValue>
                  name="password"
                  label="Mot de passe"
                />
              </FormField>
            </FormRow>
            <FormAction className={styles.buttons}>
              <Link className={styles.link} to="/forgot-password">
                Mot de passe oublié
              </Link>
              <Button
                type="submit"
                disabled={isSubmitting}
                icon={isSubmitting ? <Loader /> : undefined}
              >
                Se connecter
              </Button>
            </FormAction>
          </FormGrid>
        </Form>
      </MaxWidthContent>
    </UnconnectedPageTemplate>
  );
}
